import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TrackingData {
    exid: string | null;
    tfid: string | null;
    clickid: string | null;
    cmp: string | null;
    isLoaded: boolean;
}

interface TrackingContextType {
    trackingData: TrackingData;
    isPolling: boolean;
}

interface TrackingProviderProps {
    children: ReactNode;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

export function TrackingProvider({ children }: TrackingProviderProps): JSX.Element {
    const getBrowserCookie = (key: string): string | null => {
        if (!key) return null;
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return null;
        }
        
        const name = key + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    };

    const getAllTrackingCookies = (): TrackingData => {
        if (typeof window === 'undefined') {
            return {
                exid: null,
                tfid: null,
                clickid: null,
                cmp: null,
                isLoaded: false
            };
        }
        
        return {
            exid: getBrowserCookie('u'),
            tfid: getBrowserCookie('tfid'),
            clickid: getBrowserCookie('clickid'),
            cmp: getBrowserCookie('cmp'),
            isLoaded: true
        };
    };

    const [trackingData, setTrackingData] = useState<TrackingData>(() => getAllTrackingCookies());
    const [isPolling, setIsPolling] = useState(false);

    // Poll for cookies until they're all loaded or timeout
    useEffect(() => {
        if (typeof window === 'undefined') return;

        let pollInterval: NodeJS.Timeout;
        let timeoutId: NodeJS.Timeout;
        let pollCount = 0;
        const maxPolls = 5;
        
        const checkForCookies = () => {
            const currentData = getAllTrackingCookies();
            setTrackingData(currentData);
            
            pollCount++;
            
            // Stop polling if we have both exid and tfid or reached max polls
            const hasBothRequiredCookies = currentData.exid && currentData.tfid;
            
            if (hasBothRequiredCookies || pollCount >= maxPolls) {
                if (pollInterval) clearInterval(pollInterval);
                if (timeoutId) clearTimeout(timeoutId);
                setIsPolling(false);
            }
        };

        // Initial check
        checkForCookies();
        
        // Only start polling if we don't have both required cookies yet
        const initialData = getAllTrackingCookies();
        const hasBothInitialCookies = initialData.exid && initialData.tfid;
        
        if (!hasBothInitialCookies) {
            setIsPolling(true);
            // Poll every 500ms for cookies
            pollInterval = setInterval(checkForCookies, 500);
            
            // Cleanup after 15 seconds regardless
            timeoutId = setTimeout(() => {
                if (pollInterval) clearInterval(pollInterval);
                setIsPolling(false);
            }, 15000);
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval);
            if (timeoutId) clearTimeout(timeoutId);
            setIsPolling(false);
        };
    }, []);

    const contextValue: TrackingContextType = {
        trackingData,
        isPolling
    };
    return (
        <TrackingContext.Provider value={contextValue}>
            {children}
        </TrackingContext.Provider>
    );
}


export function useTracking(): TrackingContextType {
    const context = useContext(TrackingContext);
    if (!context) {
        throw new Error('useTracking must be used within a TrackingProvider');
    }
    return context;
}
